import { formatDecimal, formatInteger, formatPercent } from '../../shared/format.ts';
import type { TransitionDisplayMode, TransitionMatrixResult } from '../../analytics/memory-states.ts';

export function TransitionHeatmap(props: { matrix: TransitionMatrixResult; mode: TransitionDisplayMode }) {
  const labels = props.matrix.labels;
  return (
    <div className="table-scroll">
      <table className="min-w-[720px] w-full text-left text-sm">
        <thead className="border-b border-line text-xs text-muted">
          <tr>
            <th className="px-2 py-2 font-medium">From \\ To</th>
            {labels.map((label) => (
              <th key={label} className="px-2 py-2 font-medium">
                {label}
              </th>
            ))}
            <th className="px-2 py-2 font-medium">n</th>
          </tr>
        </thead>
        <tbody>
          {props.matrix.rows.map((row) => (
            <tr key={row.from} className="border-b border-line/70">
              <th className="px-2 py-2 text-left font-medium">{row.from}</th>
              {row.cells.map((cell) => {
                const display = cellDisplay(cell, props.mode);
                return (
                  <td
                    key={`${cell.from}-${cell.to}`}
                    className="px-2 py-2 text-center"
                    style={{ backgroundColor: cellColor(display.intensity, display.sign) }}
                  >
                    <div>{display.text}</div>
                    <div className="text-[10px] text-muted">n={formatInteger(cell.count)}</div>
                  </td>
                );
              })}
              <td className="px-2 py-2 text-muted">{formatInteger(row.sampleSize)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function cellDisplay(
  cell: TransitionMatrixResult['rows'][number]['cells'][number],
  mode: TransitionDisplayMode,
): { text: string; intensity: number; sign: number } {
  if (mode === 'observed') {
    return {
      text: formatPercent(cell.observedProbability, 1),
      intensity: cell.observedProbability ?? 0,
      sign: 1,
    };
  }
  if (mode === 'expected') {
    return {
      text: formatPercent(cell.expectedProbability, 1),
      intensity: cell.expectedProbability ?? 0,
      sign: 1,
    };
  }
  if (mode === 'difference_pp') {
    const pp = cell.differencePercentagePoints;
    return {
      text: pp === null ? '—' : `${pp > 0 ? '+' : ''}${formatDecimal(pp, 1)} pp`,
      intensity: Math.min(1, Math.abs(pp ?? 0) / 20),
      sign: Math.sign(pp ?? 0),
    };
  }
  return {
    text: formatDecimal(cell.observedOverExpected, 2),
    intensity:
      cell.observedOverExpected === null
        ? 0
        : Math.min(1, Math.abs(Math.log(Math.max(cell.observedOverExpected, 1e-6))) / 1.5),
    sign: (cell.observedOverExpected ?? 1) >= 1 ? 1 : -1,
  };
}

function cellColor(intensity: number, sign: number): string {
  const alpha = Math.max(0.04, Math.min(0.35, intensity));
  if (sign < 0) {
    return `rgba(143, 61, 61, ${alpha})`;
  }
  return `rgba(63, 93, 86, ${alpha})`;
}
